import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import RichTextEditor from '../trainer/RichTextEditor';

const PlanNotes = ({ planId, userRole = 'owner' }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, trainer, owner

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_pinned: false
  });

  const fetchNotes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('plan_notes_20250218')
        .select('*')
        .eq('plan_id', planId)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    fetchNotes();
    const channel = supabase
      .channel(`plan_notes_${planId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'plan_notes_20250218', filter: `plan_id=eq.${planId}` }, fetchNotes)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [planId, fetchNotes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const noteData = {
        ...formData,
        plan_id: planId,
        author_role: userRole
      };

      if (editingNote) {
        await supabase.from('plan_notes_20250218').update(noteData).eq('id', editingNote.id);
      } else {
        await supabase.from('plan_notes_20250218').insert([noteData]);
      }

      setShowForm(false);
      setEditingNote(null);
      setFormData({ title: '', content: '', is_pinned: false });
      fetchNotes();
    } catch (err) {
      alert('Failed to save note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await supabase.from('plan_notes_20250218').delete().eq('id', id);
      fetchNotes();
    } catch (err) {
      alert('Delete failed');
    }
  };

  const filteredNotes = notes.filter(n => {
    if (filter === 'all') return true;
    return n.author_role === filter;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex bg-gray-100 p-1 rounded-xl">
          <button onClick={() => setFilter('all')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${filter === 'all' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-500'}`}>All Notes</button>
          <button onClick={() => setFilter('trainer')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${filter === 'trainer' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-500'}`}>Trainer Resources</button>
          <button onClick={() => setFilter('owner')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${filter === 'owner' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-500'}`}>My Journal</button>
        </div>
        
        <button 
          onClick={() => { setShowForm(true); setEditingNote(null); setFormData({ title: '', content: '', is_pinned: false }); }}
          className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 hover:bg-indigo-700 shadow-lg shadow-indigo-100 active:scale-95"
        >
          <SafeIcon name="Plus" /> {userRole === 'trainer' ? 'Add Resource' : 'Add Journal Entry'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2].map(i => <div key={i} className="h-32 bg-white rounded-3xl border border-gray-100" />)}
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="bg-white p-16 text-center rounded-[2.5rem] border-2 border-dashed border-gray-100">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
              <SafeIcon name="FileText" className="w-8 h-8" />
            </div>
            <p className="text-gray-400 font-bold">No notes found here yet.</p>
          </div>
        ) : (
          filteredNotes.map(note => (
            <motion.div 
              layout
              key={note.id} 
              className={`bg-white rounded-3xl border-2 p-6 transition-all shadow-sm ${note.is_pinned ? 'border-indigo-100 bg-indigo-50/10' : 'border-gray-50 hover:border-indigo-50'}`}
            >
              <div className="flex justify-between items-start gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {note.is_pinned && <SafeIcon name="Award" className="w-4 h-4 text-indigo-600" />}
                    <h3 className="text-xl font-black text-gray-900">{note.title}</h3>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest">
                    <span className={`px-2 py-0.5 rounded-md ${note.author_role === 'trainer' ? 'bg-indigo-100 text-indigo-700' : 'bg-green-100 text-green-700'}`}>
                      {note.author_role === 'trainer' ? 'Trainer Resource' : 'My Journal'}
                    </span>
                    <span className="text-gray-300">•</span>
                    <span className="text-gray-400">{format(new Date(note.created_at), 'MMM d, yyyy • h:mm a')}</span>
                  </div>
                </div>
                {(userRole === 'trainer' || (userRole === 'owner' && note.author_role === 'owner')) && (
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingNote(note); setFormData(note); setShowForm(true); }} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-xl transition-all">
                      <SafeIcon name="Edit" className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(note.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-white rounded-xl transition-all">
                      <SafeIcon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="prose prose-indigo max-w-none text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: note.content }} />
            </motion.div>
          ))
        )}
      </div>

      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                <h3 className="text-xl font-black text-gray-900">{editingNote ? 'Edit Entry' : 'New Entry'}</h3>
                <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-200 rounded-full"><SafeIcon name="X" /></button>
              </div>
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Title</label>
                  <input required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" placeholder="Enter a descriptive title..." />
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Content & Attachments</label>
                  <RichTextEditor value={formData.content} onChange={val => setFormData({...formData, content: val})} placeholder="Write your notes, embed videos, or paste links..." />
                </div>

                <label className="flex items-center gap-3 cursor-pointer group p-2">
                  <input type="checkbox" checked={formData.is_pinned} onChange={e => setFormData({...formData, is_pinned: e.target.checked})} className="w-5 h-5 text-indigo-600 border-gray-300 rounded-lg focus:ring-indigo-500" />
                  <span className="text-sm font-bold text-gray-700 group-hover:text-indigo-600 transition-colors">Pin to top of list</span>
                </label>
              </form>
              <div className="p-6 border-t bg-gray-50 flex gap-4">
                <button onClick={handleSubmit} disabled={isSubmitting} className="flex-1 bg-indigo-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Save Entry'}
                </button>
                <button onClick={() => setShowForm(false)} className="flex-1 bg-white border-2 border-gray-100 py-4 rounded-2xl font-black text-gray-500 hover:bg-gray-100 transition-all">Cancel</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlanNotes;